import React, { useEffect, useMemo, useState } from "react";
import config from "@config";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import useApiResource from "../../useApiResource";
import ConfirmModal from "../ConfirmModal";
import ReturnAddressModal from "../ReturnAddressModal";
import {
  FormSection,
  Field,
  EditableField,
  FieldGrid,
  FieldError,
  SelectControl,
  TextAreaField,
  CheckboxGrid,
  ToggleRow,
  YesNoField,
  RadioListField,
  Dropzone,
} from "../FormBits";
import { FAULT_OPTIONS } from "../../mock/caseDetail";
import {
  COSMETIC_CONDITION_OPTIONS,
  SUBMITTER_TYPE_OPTIONS,
  toFaultTypeCode,
} from "../../NewCase/options";

// Matches the accepted types on the "Add Picture/Video/PDF" upload in
// Warranties/SingleWarranty/Activities.js so both flows reject the same files.
const ALLOWED_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "application/pdf",
];

// True/false has no natural text form for a "#"-labelled field, so it's
// spelled out rather than left as a raw boolean.
const yesNo = function (value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return undefined;
};

const joinAddress = function (parts) {
  const filled = parts.filter(function (part) {
    return part && String(part).trim();
  });
  return filled.length ? filled.join(", ") : undefined;
};

/*
GET /ticket/{id}/ backs every field below except POR (proof of return #),
Previous Cases and the "Return delivery to store?" toggle - backend confirmed
(2026-09-25) none of these have a model field yet, so they're left blank
rather than guessed at. The retailer address is `pickup_address`, the
one-line retailer address the create flow sends (NewCase/mappers.js).

Editable fields:
- The fault description, retailer contact/address, primary contact and
  Solvup Case # go into the page's Save draft (`draft`, PATCH /ticket/{id}/ -
  see useTicketDraft.js), so they're saved together with the other tabs' edits.
- Store Location keeps its own endpoint (ticket.changeLocation, as
  TicketProcess.js) and inline Save - it isn't a Save field.
- The return address keeps ticket.address (as DeliveryAddress.js): it carries
  `country`, which Save rejects.
Customer and device details aren't editable through Save, so they stay
read-only.
*/
const ProductTab = function (props) {
  const { protectedFetch, reloadTicket, draft } = props;
  const { showToast } = useToast();
  const ticket = props.ticket || {};
  const userDevice = ticket.user_device || {};
  const device = userDevice.device || {};
  const brandId = device.brand && device.brand.id;

  const canEdit = !!props.canEdit;
  const lockDraft = !canEdit || draft.saving;
  // A Save-draft field's props for a FormBits control.
  const draftField = function (key) {
    return Object.assign({ id: "ticketProduct_" + key, disabled: lockDraft }, draft.bind(key));
  };

  const [saving, setSaving] = useState(null); // null | "storeLocation" | "address"
  const [storeLocationId, setStoreLocationId] = useState("");
  const [confirmStoreLocation, setConfirmStoreLocation] = useState(false);
  const [showAddressEdit, setShowAddressEdit] = useState(false);

  // Resync the draft whenever a reload brings back a new saved value.
  useEffect(
    function () {
      setStoreLocationId(ticket.store_location == null ? "" : String(ticket.store_location));
    },
    [ticket.store_location]
  );

  const faultTypes = draft.value("fault_types");
  const toggleFault = function (option, checked) {
    const code = toFaultTypeCode(option).toLowerCase();
    const rest = faultTypes.filter(function (value) {
      return value !== code;
    });
    draft.set("fault_types", checked ? rest.concat(code) : rest);
  };

  // The "Please specify" text only means something alongside "other", as in
  // the create flow (NewCase/mappers.js), so it's cleared when that's dropped.
  const cosmeticCondition = draft.value("cosmetic_condition");
  const setCosmeticCondition = function (next) {
    draft.set("cosmetic_condition", next);
    if (next !== "other") draft.set("cosmetic_condition_other", "");
  };

  // Same list as retrieveStoreLocation in SingleWarranty.js.
  const storeLocations = useApiResource(
    protectedFetch,
    apiEndpoints.storeLocation.dropdown(),
    { brandId: brandId },
    { skip: !ticket.id }
  );
  // String ids, to match `storeLocationId` - SelectControl compares with ===.
  const storeLocationOptions = (Array.isArray(storeLocations.data) ? storeLocations.data : []).map(
    function (location) {
      return { value: String(location.id), label: location.seller_name + " - " + location.name };
    }
  );
  const selectedStoreLocation = storeLocationOptions.find(function (option) {
    return option.value === storeLocationId;
  });

  const returnAddress = useMemo(
    function () {
      return {
        address_line_one: ticket.address_line_one || "",
        address_line_two: ticket.address_line_two || "",
        suburb: ticket.suburb || "",
        state: ticket.state || "",
        postcode: ticket.postcode || "",
        country: ticket.country || config.country,
      };
    },
    [
      ticket.address_line_one,
      ticket.address_line_two,
      ticket.suburb,
      ticket.state,
      ticket.postcode,
      ticket.country,
    ]
  );

  // Resolves true on success so callers can close their modal.
  const patchTicket = function (key, url, payload, messages) {
    setSaving(key);
    return protectedFetch(url, "PATCH", payload, errorFunctions.checkResponseThenJson)
      .then(function () {
        showToast(messages.success, "success");
        if (reloadTicket) reloadTicket();
        return true;
      })
      .catch(function (err) {
        console.log("fail", err);
        showToast(messages.error, "error");
        return false;
      })
      .finally(function () {
        setSaving(null);
      });
  };

  const saveStoreLocation = function () {
    patchTicket(
      "storeLocation",
      apiEndpoints.ticket.changeLocation(ticket.id),
      { store_location: Number(storeLocationId) },
      { success: "Store location updated", error: "Failed to update store location" }
    ).then(function (ok) {
      if (ok) setConfirmStoreLocation(false);
    });
  };

  const saveReturnAddress = function (draft) {
    patchTicket(
      "address",
      apiEndpoints.ticket.address(ticket.id),
      {
        address_line_one: draft.address_line_one || "",
        address_line_two: draft.address_line_two || "",
        suburb: draft.suburb || "",
        state: draft.state || "",
        postcode: draft.postcode || "",
        country: draft.country || config.country,
      },
      { success: "Return address updated", error: "Failed to update return address" }
    ).then(function (ok) {
      if (ok) setShowAddressEdit(false);
    });
  };

  // No attachment endpoint exists for this prototype yet (see file-level
  // note in TicketCaseDetail.js), so a save just keeps the file client-side
  // via an object URL rather than posting it anywhere.
  const [localAttachments, setLocalAttachments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [allowUpload, setAllowUpload] = useState(false);
  const [mediaTypeWarning, setMediaTypeWarning] = useState("");
  const [uploadKey, setUploadKey] = useState(0);

  const resetMediaSelection = function () {
    setSelectedFile(null);
    setAllowUpload(false);
    setMediaTypeWarning("");
    // Remounts the file input so a re-pick fires onChange even for the same
    // path, and so a cleared/saved selection doesn't linger in the input.
    setUploadKey(function (key) {
      return key + 1;
    });
  };

  const handleFileChange = function (e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_MEDIA_TYPES.includes(file.type)) {
      setAllowUpload(false);
      setSelectedFile(null);
      setMediaTypeWarning(
        "Only JPG, JPEG, PNG, WEBM, MOV, MP4 and PDF files are allowed."
      );
    } else {
      setAllowUpload(true);
      setSelectedFile(file);
      setMediaTypeWarning("");
    }
  };

  const handleSaveMedia = function () {
    if (!allowUpload || !selectedFile) return;
    setLocalAttachments(function (prev) {
      return [
        {
          id: "local-" + Date.now(),
          file_name: selectedFile.name,
          file_url: URL.createObjectURL(selectedFile),
        },
        ...prev,
      ];
    });
    resetMediaSelection();
  };

  const details = [
    { label: "Product Name", placeholder: "e.g. Dreame L20 Ultra", value: device.name },
    { label: "SKU", placeholder: "SKU code", value: device.sku },
    { label: "Serial Number", placeholder: "S/N", value: userDevice.serial_number },
    {
      label: "POP",
      placeholder: "Proof of purchase #",
      value: userDevice.proof_text || yesNo(userDevice.proof_of_purchase),
    },
    { label: "POR", placeholder: "Proof of return #" },
  ];

  const retailer = [
    { label: "Retailer Name", placeholder: "e.g. Harvey Norman", value: ticket.retailer },
    Object.assign(
      { label: "Contact", placeholder: "Contact number", type: "tel" },
      draftField("retailer_phone")
    ),
    Object.assign(
      { label: "Email", placeholder: "name@retailer.com", type: "email" },
      draftField("retailer_email")
    ),
  ];

  // The create flow only ever wrote replication_steps for retailers; a
  // customer's own description is in other_issue, shown alongside for context.
  const customerDescription =
    ticket.other_issue && ticket.other_issue !== draft.value("replication_steps")
      ? "Customer's description: " + ticket.other_issue
      : null;

  const customer = [
    { label: "Customer Name", placeholder: "Full name", value: userDevice.name },
    { label: "Contact", placeholder: "Phone number", value: userDevice.mobile },
    { label: "Email", placeholder: "name@email.com", value: userDevice.email },
  ];

  const customerAddress = joinAddress([
    userDevice.address_line_one,
    userDevice.address_line_two,
    userDevice.suburb,
    userDevice.state,
    userDevice.postcode,
  ]);

  const returnAddressText = joinAddress([
    returnAddress.address_line_one,
    returnAddress.address_line_two,
    returnAddress.suburb,
    returnAddress.state,
    returnAddress.postcode,
  ]);

  return (
    <div className="tn-tabpanel">
      <FormSection title="Product Details">
        <FieldGrid fields={details} />
      </FormSection>

      <FormSection title="Fault Description">
        <CheckboxGrid
          options={FAULT_OPTIONS}
          selected={faultTypes}
          onToggle={toggleFault}
          disabled={lockDraft}
          error={draft.fieldErrors.fault_types}
        />
        <TextAreaField
          label="Details"
          placeholder="Describe the reported fault in detail…"
          hint={customerDescription}
          {...draftField("replication_steps")}
        />
        <div className="tn-fieldgrid tn-fieldgrid-2">
          <YesNoField label="Fault replicated in store?" {...draftField("fault_replicated_in_store")} />
          <YesNoField label="Water & debris removed?" {...draftField("water_debris_removed")} />
        </div>
        <RadioListField
          label="Cosmetic Condition - Is there any visible physical damage to the product?"
          options={COSMETIC_CONDITION_OPTIONS}
          {...draftField("cosmetic_condition")}
          onChange={setCosmeticCondition}
        />
        {cosmeticCondition === "other" ? (
          <Field
            label="Please specify"
            placeholder="Describe the cosmetic condition"
            {...draftField("cosmetic_condition_other")}
          />
        ) : null}
        <Field
          label="Missing Accessories"
          placeholder="e.g. Charging dock, mop pads"
          {...draftField("missing_accessories")}
        />
        <TextAreaField
          label="Troubleshooting Details"
          placeholder="Troubleshooting done before the case was raised"
          {...draftField("commercial_troubleshooting_details")}
        />
      </FormSection>

      <FormSection title="Attachments">
        <Dropzone
          hint="Photos, PDFs or videos · up to 25 MB each"
          files={localAttachments.concat(ticket.attachments || [])}
          inputId="ticketProductMedia"
          inputKey={uploadKey}
          accept="image/jpeg, image/png, video/mp4, video/webm, video/quicktime, application/pdf"
          selectedFileName={selectedFile ? selectedFile.name : ""}
          warning={mediaTypeWarning}
          saveDisabled={!allowUpload}
          onFileChange={handleFileChange}
          onSave={handleSaveMedia}
          onClear={resetMediaSelection}
        />
      </FormSection>

      <FormSection title="Retailer">
        <FieldGrid fields={retailer} />
        <EditableField
          id="ticketProductStoreLocation"
          label="Store Location"
          placeholder="Search for a store location…"
          options={storeLocationOptions}
          loading={storeLocations.loading}
          value={storeLocationId}
          onChange={setStoreLocationId}
          dirty={
            storeLocationId !== "" &&
            storeLocationId !== String(ticket.store_location == null ? "" : ticket.store_location)
          }
          saving={saving === "storeLocation"}
          disabled={!canEdit}
          onSave={function () {
            setConfirmStoreLocation(true);
          }}
        />
        <TextAreaField
          label="Address"
          placeholder="Retailer address"
          {...draftField("pickup_address")}
        />
        <div className="tn-fieldgrid tn-fieldgrid-3">
          <Field label="Solvup Case #" placeholder="Case reference" {...draftField("solvup_id")} />
          <div className="tn-field">
            <label className="tn-field-label" htmlFor="ticketProduct_primary_email">
              Primary Contact
            </label>
            <SelectControl
              inputId="ticketProduct_primary_email"
              options={SUBMITTER_TYPE_OPTIONS}
              value={draft.value("primary_email")}
              onChange={function (value) {
                draft.set("primary_email", value);
              }}
              isDisabled={lockDraft}
            />
            <FieldError error={draft.fieldErrors.primary_email} />
          </div>
          <Field label="Previous Cases" placeholder="Linked case IDs" />
        </div>
        <ToggleRow label="Return delivery to store?" />
      </FormSection>

      <FormSection title="Customer">
        <FieldGrid fields={customer} />
        <TextAreaField
          label="Address"
          placeholder="Customer address"
          value={customerAddress}
        />
      </FormSection>

      <FormSection title="Return Address">
        {/* Keyed on the text: the textarea is uncontrolled, so it only picks
            up a newly saved address on remount. */}
        <TextAreaField
          key={returnAddressText || "no-return-address"}
          label="Address"
          placeholder="No return address on this ticket"
          value={returnAddressText}
          action={
            canEdit ? (
              <button
                type="button"
                className="tn-btn tn-btn-ghost tn-btn-sm"
                onClick={function () {
                  setShowAddressEdit(true);
                }}
              >
                Edit
              </button>
            ) : null
          }
        />
      </FormSection>

      <ConfirmModal
        open={confirmStoreLocation}
        title="Assign store location"
        message={
          selectedStoreLocation
            ? 'Assign this ticket to "' + selectedStoreLocation.label + '"?'
            : null
        }
        confirmLabel="Assign"
        busy={saving === "storeLocation"}
        onConfirm={saveStoreLocation}
        onCancel={function () {
          if (saving !== "storeLocation") setConfirmStoreLocation(false);
        }}
      />

      {showAddressEdit ? (
        <ReturnAddressModal
          address={returnAddress}
          busy={saving === "address"}
          protectedFetch={protectedFetch}
          onSave={saveReturnAddress}
          onClose={function () {
            if (saving !== "address") setShowAddressEdit(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default ProductTab;
